/**
 * Amounts of a reacting species: a mass, a number of moles, or a volume of gas, and the
 * conditions that turn a gas volume into moles.
 *
 * ## Gas volumes
 *
 * Molar volume of an ideal gas is V = RT/P. With R = 8.314 462 618… J/(mol·K), which is
 * exact since the 2019 SI redefinition (NIST CODATA), and P in kilopascals, V in litres per
 * mole is simply R·T/P. The standard conditions are:
 *
 * - IUPAC STP, since 1982: 273.15 K and 100 kPa (10⁵ Pa) → 22.711 L/mol;
 * - the older convention, 273.15 K and 1 atm = 101.325 kPa → 22.414 L/mol (the NIST value
 *   22.413 969 54 L/mol is exact for these conditions);
 * - SATP, 298.15 K and 100 kPa → 24.790 L/mol; and 298.15 K and 1 atm → 24.465 L/mol.
 */

import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

export const GAS_CONSTANT = 8.314_462_618_153_24;

export interface GasPreset {
  id: string;
  kelvin: number;
  kilopascals: number;
}

export const GAS_PRESETS: readonly GasPreset[] = [
  { id: "stp", kelvin: 273.15, kilopascals: 100 },
  { id: "stpAtm", kelvin: 273.15, kilopascals: 101.325 },
  { id: "satp", kelvin: 298.15, kilopascals: 100 },
  { id: "roomAtm", kelvin: 298.15, kilopascals: 101.325 },
];

export const PRESSURE_UNITS: readonly {
  id: string;
  symbol: string;
  kilopascals: number;
}[] = [
  { id: "kPa", symbol: "kPa", kilopascals: 1 },
  { id: "atm", symbol: "atm", kilopascals: 101.325 },
  { id: "bar", symbol: "bar", kilopascals: 100 },
  { id: "mmHg", symbol: "mmHg", kilopascals: 0.133_322_387_415 },
];

export const TEMPERATURE_UNITS = [
  { id: "C", symbol: "°C" },
  { id: "K", symbol: "K" },
] as const;

const ZERO_CELSIUS = 273.15;

export function toKelvin(value: number, unit: string): number {
  return unit === "K" ? value : value + ZERO_CELSIUS;
}

export function toKilopascals(value: number, unit: string): number {
  const found = PRESSURE_UNITS.find((u) => u.id === unit) ?? PRESSURE_UNITS[0];
  return value * found.kilopascals;
}

/** Litres of ideal gas per mole at `kelvin` and `kilopascals`; null for non-positive input. */
export function molarVolume(
  kelvin: number,
  kilopascals: number
): number | null {
  if (!(kelvin > 0 && kilopascals > 0)) {
    return null;
  }
  return (GAS_CONSTANT * kelvin) / kilopascals;
}

/* ------------------------------------------------------------------ amounts */

export type AmountKind = "mass" | "amount" | "volume";

export interface AmountUnit {
  id: string;
  symbol: string;
  kind: AmountKind;
  /** Size of one unit in g, mol or L. */
  factor: number;
}

export const AMOUNT_UNITS: readonly AmountUnit[] = [
  { id: "mg", symbol: "mg", kind: "mass", factor: 1e-3 },
  { id: "g", symbol: "g", kind: "mass", factor: 1 },
  { id: "kg", symbol: "kg", kind: "mass", factor: 1e3 },
  { id: "mmol", symbol: "mmol", kind: "amount", factor: 1e-3 },
  { id: "mol", symbol: "mol", kind: "amount", factor: 1 },
  { id: "mL", symbol: "mL", kind: "volume", factor: 1e-3 },
  { id: "L", symbol: "L", kind: "volume", factor: 1 },
];

export function findAmountUnit(id: string): AmountUnit {
  return AMOUNT_UNITS.find((unit) => unit.id === id) ?? AMOUNT_UNITS[1];
}

export type MolesResult =
  | { ok: true; moles: number }
  | { ok: false; reason: "noMolarMass" | "noGas" };

/**
 * Moles from a typed amount. A mass needs the molar mass (`null` for the electron); a gas
 * volume needs the molar volume (`null` when the conditions are not usable).
 */
export function molesFrom(
  value: number,
  unitId: string,
  molarMass: number | null,
  litresPerMole: number | null
): MolesResult {
  const unit = findAmountUnit(unitId);
  const base = value * unit.factor;
  if (unit.kind === "amount") {
    return { ok: true, moles: base };
  }
  if (unit.kind === "mass") {
    return molarMass === null
      ? { ok: false, reason: "noMolarMass" }
      : { ok: true, moles: base / molarMass };
  }
  return litresPerMole === null
    ? { ok: false, reason: "noGas" }
    : { ok: true, moles: base / litresPerMole };
}

export interface GasConditionsInput {
  /** A preset id from `GAS_PRESETS`, or "custom". */
  preset: string;
  temperature: string;
  temperatureUnit: string;
  pressure: string;
  pressureUnit: string;
}

export const DEFAULT_GAS_CONDITIONS: GasConditionsInput = {
  preset: "stp",
  temperature: "25",
  temperatureUnit: "C",
  pressure: "101.325",
  pressureUnit: "kPa",
};

/** Litres per mole for the chosen conditions; null when a custom entry is unusable. */
export function resolveMolarVolume(input: GasConditionsInput): number | null {
  const preset = GAS_PRESETS.find((p) => p.id === input.preset);
  if (preset) {
    return molarVolume(preset.kelvin, preset.kilopascals);
  }
  const temperature = parseMeasuredNumber(input.temperature);
  const pressure = parseMeasuredNumber(input.pressure);
  if (temperature === null || pressure === null) {
    return null;
  }
  return molarVolume(
    toKelvin(temperature, input.temperatureUnit),
    toKilopascals(pressure, input.pressureUnit)
  );
}

/** The inverse of `molesFrom`: moles written in `unitId`; null when the unit cannot be used. */
export function amountFromMoles(
  moles: number,
  unitId: string,
  molarMass: number | null,
  litresPerMole: number | null
): number | null {
  const unit = findAmountUnit(unitId);
  if (unit.kind === "amount") {
    return moles / unit.factor;
  }
  if (unit.kind === "mass") {
    return molarMass === null ? null : (moles * molarMass) / unit.factor;
  }
  return litresPerMole === null ? null : (moles * litresPerMole) / unit.factor;
}
